
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom'; 
import { 
    Factory, Activity, Layers, ChevronRight, ChevronLeft,
    ArrowLeft, ArrowRightLeft, X, Clock, IndianRupee,
    CalendarDays, TrendingUp, Sparkles, AlertCircle,
    Sun, Moon
} from 'lucide-react';

const API_BASE = 'https://mukadampro.shirpur.store';

const MemoizedLoomBox = React.memo(({ loom, loomIdLabel, isActive, workerShiftNameHindi, isNightWorker, workerShiftProd, loomTimeFilter }) => {});

// --- HELPER: TIME AGO ---
const isLoomActive = (lastUpdated) => {
  if (!lastUpdated) return false;
  const updated = new Date(lastUpdated.replace(' ', 'T')); 
  const now = new Date();
  const diffMinutes = (now - updated) / 1000 / 60;
  return !isNaN(diffMinutes) && diffMinutes < 10; 
};


// --- HELPER: FORMAT TIME ---
const formatTimeForDisplay = (t) => { 
    if (!t) return ""; 
    let timeStr = String(t).trim().replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    if (timeStr.toUpperCase().includes('PM') || timeStr.toUpperCase().includes('AM')) return timeStr; 
    let [h, m] = timeStr.split(':'); 
    h = parseInt(h, 10) || 0; 
    const ampm = h >= 12 ? 'PM' : 'AM'; 
    return `${h % 12 || 12}:${m || '00'} ${ampm}`; 
};

// 🌟 NAYA: PARSE TIME STR (Duty duration nikalne ke liye zaroori)
const parseTimeStr = (t) => { 
    if (!t) return 0;
    let timeStr = String(t).trim();
    timeStr = timeStr.replace(/^\d{4}-\d{2}-\d{2}\s+/, '');
    
    let isPM = timeStr.toUpperCase().includes('PM');
    let isAM = timeStr.toUpperCase().includes('AM');
    
    let cleanTime = timeStr.replace(/PM|AM/i, '').trim();
    let [h, m] = cleanTime.split(':').map(Number);
    
    h = h || 0;
    m = m || 0;
    
    if (isPM && h !== 12) h += 12;
    if (isAM && h === 12) h = 0;
    
    return h * 60 + m; 
};

// --- HELPER: CALCULATE EXACT TOTAL DAYS ---
const calculateTotalDays = (dojStr) => {
    const today = new Date();
    let start = dojStr ? new Date(dojStr) : new Date('2024-01-01');
    if (isNaN(start.getTime())) start = new Date('2024-01-01');
    
    start.setHours(0, 0, 0, 0);
    const current = new Date(today);
    current.setHours(0, 0, 0, 0);
    
    const diffTime = Math.abs(current - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; 
};

// ===== 🌟 PREMIUM STAT CARD COMPONENT 🌟 =====
const StatCard = ({ label, value, icon, gradient, iconBg, shadowColor, onClick, subtitle }) => (
  <div 
    onClick={onClick}
    style={{
        background: 'rgba(255, 255, 255, 0.95)', 
        backdropFilter: 'blur(10px)',
        borderRadius: '20px', 
        padding: '16px',
        boxShadow: `0 8px 24px ${shadowColor || 'rgba(0,0,0,0.04)'}, inset 0 1px 2px rgba(255,255,255,0.8)`, 
        border: '1px solid rgba(255,255,255,0.5)',
        display: 'flex', flexDirection: 'column', gap: '12px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        width: '100%',
        overflow: 'hidden'
  }}>
    <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: gradient, opacity: 0.15, filter: 'blur(20px)', borderRadius: '50%', zIndex: 0 }} />

    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 1 }}>
        <div style={{ 
            background: iconBg || 'linear-gradient(135deg, #E0F2FE, #38BDF8)', 
            padding: '8px', 
            borderRadius: '12px', 
            boxShadow: shadowColor ? `0 4px 12px ${shadowColor}` : '0 4px 12px rgba(56, 189, 248, 0.25)', 
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff'
        }}>
            {icon}
        </div>
        <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
          {String(label)}
        </span>
    </div>

    <div style={{ zIndex: 1 }}>
        {value}
    </div>
    {subtitle && <div style={{ zIndex: 1 }}>{subtitle}</div>}
  </div>
);


// --- COMPONENT: STATS MODAL (Full Screen Glassmorphism via React Portal) ---
const StatsModal = ({ title, data, period, onClose, appBg, handleVoiceClick, workerLooms, workerShift, myLiveApiData }) => {
  const [selectedYearForMonths, setSelectedYearForMonths] = useState(null); 
  const [selectedMonthForDays, setSelectedMonthForDays] = useState(null);
  const [selectedWeekForDays, setSelectedWeekForDays] = useState(null); 
  const [selectedDayForLooms, setSelectedDayForLooms] = useState(null); 
  const ALL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const [currentMonthTab, setCurrentMonthTab] = useState(ALL_MONTHS[new Date().getMonth()]); 

  const currentRowRef = React.useRef(null);
  const activeMonthTabRef = React.useRef(null);

  useEffect(() => {
      if (currentRowRef.current) currentRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [period, selectedYearForMonths, selectedMonthForDays, currentMonthTab]);

  useEffect(() => {
      if (activeMonthTabRef.current) activeMonthTabRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center' });
  }, [currentMonthTab]);

  const workerFilteredData = useMemo(() => {
      if (!workerLooms || workerLooms.length === 0) {
          return { reports: { daily_wise: {}, month_wise: {}, year_wise: {} }, weekly: {} };
      }
      const myLoomIds = workerLooms.map(l => String(l.loom_id || l.loom_no || l.id).replace(/[^0-9]/g, ''));
      let newReports = { daily_wise: {}, month_wise: {}, year_wise: {} };
      let newWeekly = {};
      
      const extractWorkerData = (dayObj) => {
          let total = 0; let looms = {};
          if (dayObj && dayObj.looms) {
              Object.entries(dayObj.looms).forEach(([lId, lVal]) => {
                  const cleanId = String(lId).replace(/[^0-9]/g, '');
                  total += parseFloat(lVal || 0);
                  looms[cleanId] = lVal;
              });
          }
          return { total, looms };
      };

      if (data?.reports?.daily_wise) {
          Object.entries(data.reports.daily_wise).forEach(([year, months]) => {
              newReports.daily_wise[year] = {}; newReports.month_wise[year] = {};
              let yearTotal = 0; let yearDay = 0; let yearNight = 0;
              
              Object.entries(months).forEach(([month, days]) => {
                  newReports.daily_wise[year][month] = {};
                  let monthTotal = 0; let monthDay = 0; let monthNight = 0;
                  
                  Object.entries(days).forEach(([date, dayData]) => {
                      const { total, looms } = extractWorkerData(dayData);
                      if (total > 0) {
                          let dayMeters = workerShift === 'Day' ? total : (workerShift === 'Night' ? 0 : total / 2);
                          let nightMeters = workerShift === 'Night' ? total : (workerShift === 'Day' ? 0 : total / 2);

                          newReports.daily_wise[year][month][date] = { Total: total, Day: dayMeters, Night: nightMeters, looms: looms };
                          monthTotal += total; monthDay += dayMeters; monthNight += nightMeters;
                      }
                  });
                 if (monthTotal > 0) {
                      newReports.month_wise[year][month] = { Total: monthTotal, Day: monthDay, Night: monthNight };
                      yearTotal += monthTotal; yearDay += monthDay; yearNight += monthNight;
                  }
              });
              if (yearTotal > 0) newReports.year_wise[year] = { Total: yearTotal, Day: yearDay, Night: yearNight };
          });
      }
      
      if (data?.weekly) {
          Object.entries(data.weekly).forEach(([date, dayData]) => {
              const { total, looms } = extractWorkerData(dayData);
              if (total > 0) {
                  let dayMeters = workerShift === 'Day' ? total : (workerShift === 'Night' ? 0 : total / 2);
                  let nightMeters = workerShift === 'Night' ? total : (workerShift === 'Day' ? 0 : total / 2);
                  newWeekly[date] = { Total: total, Day: dayMeters, Night: nightMeters, looms };
              }
          });
      }
      return { reports: newReports, weekly: newWeekly };
  }, [data, workerLooms, workerShift]);

  const reportData = useMemo(() => {
    const resultRows = [];
    const todayObj = new Date();
    const currentYearStr = todayObj.getFullYear().toString();
    const currentMonthStr = ALL_MONTHS[todayObj.getMonth()];
    const dailyWise = workerFilteredData.reports?.daily_wise || {};

    if (period === 'week') {
        const weeklyData = workerFilteredData.weekly || {};
        const startOfWeek = new Date(todayObj);
        const diffToSat = (todayObj.getDay() + 1) % 7; 
        startOfWeek.setDate(todayObj.getDate() - diffToSat);
        
        const datesToMap = [];
        for (let i=0; i<7; i++) {
            const d = new Date(startOfWeek); d.setDate(startOfWeek.getDate() + i);
            if (d > todayObj) break;
            const localOffset = d.getTimezoneOffset() * 60000;
            datesToMap.push(new Date(d.getTime() - localOffset).toISOString().split('T')[0]);
        }
        datesToMap.forEach(dateStr => {
            const r = weeklyData[dateStr] || { Day: 0, Night: 0, Total: 0, looms: {} }; 
            const d = new Date(dateStr);
            resultRows.push({ rawDate: dateStr, label: `${d.getDate()} ${d.toLocaleDateString('en-US', {month:'long'})}`, subLabel: d.toLocaleDateString('en-US', {weekday:'long'}), day: r.Day, night: r.Night, total: r.Total, looms: r.looms || {}, isCurrent: dateStr === todayObj.toLocaleDateString('fr-CA') });
        });
    } else if (period === 'month') {
        const monthData = dailyWise[currentYearStr]?.[currentMonthTab] || {};
        const monthIndex = ALL_MONTHS.indexOf(currentMonthTab);
        const isCurrentMonth = currentMonthTab === currentMonthStr;
        const daysInMonth = new Date(currentYearStr, monthIndex + 1, 0).getDate();
        
        const datesToMap = [];
        for (let i=1; i<=daysInMonth; i++) {
            const d = new Date(currentYearStr, monthIndex, i);
            if (isCurrentMonth && d > todayObj) break;
            const localOffset = d.getTimezoneOffset() * 60000;
            datesToMap.push(new Date(d.getTime() - localOffset).toISOString().split('T')[0]);
        }
        datesToMap.forEach(dateStr => {
            const r = monthData[dateStr] || { Day: 0, Night: 0, Total: 0, looms: {} }; 
            const d = new Date(dateStr);
            resultRows.push({ rawDate: dateStr, label: `${d.getDate()} ${d.toLocaleDateString('en-US', {month:'long'})}`, subLabel: d.toLocaleDateString('en-US', {weekday:'long'}), day: r.Day, night: r.Night, total: r.Total, looms: r.looms || {}, isCurrent: dateStr === todayObj.toLocaleDateString('fr-CA') });
        });
  } else if (period === 'total') {
        const reports = workerFilteredData.reports || { month_wise: {}, year_wise: {}, daily_wise: {} };
        
        if (selectedYearForMonths && selectedMonthForDays && selectedWeekForDays) {
            const wStart = new Date(selectedWeekForDays);
            for(let i=0; i<7; i++) {
                const d = new Date(wStart);
                d.setDate(wStart.getDate() + i);
                if (d > todayObj) break;

                const localOffset = d.getTimezoneOffset() * 60000;
                const dateStr = new Date(d.getTime() - localOffset).toISOString().split('T')[0];
                
                const yearStr = d.getFullYear().toString();
                const monthStr = ALL_MONTHS[d.getMonth()];
                
                const r = dailyWise[yearStr]?.[monthStr]?.[dateStr] || { Day: 0, Night: 0, Total: 0, looms: {} };
                resultRows.push({
                    rawDate: dateStr,
                    label: `${d.getDate()} ${d.toLocaleDateString('en-US', {month:'long'})}`,
                    subLabel: d.toLocaleDateString('en-US', {weekday:'long'}),
                    day: r.Day || 0,
                    night: r.Night || 0,
                    total: r.Total || 0,
                    looms: r.looms || {},
                    isCurrent: dateStr === todayObj.toLocaleDateString('fr-CA')
                });
            }
        } else if (selectedYearForMonths && selectedMonthForDays) {
            const monthData = dailyWise[selectedYearForMonths]?.[selectedMonthForDays] || {};
            const monthIndex = ALL_MONTHS.indexOf(selectedMonthForDays);
            const isCurrentMonth = selectedYearForMonths === currentYearStr && selectedMonthForDays === currentMonthStr;
            const daysInMonth = new Date(selectedYearForMonths, monthIndex + 1, 0).getDate();
            
            const weeksMap = new Map();

            for (let i = 1; i <= daysInMonth; i++) {
                const d = new Date(selectedYearForMonths, monthIndex, i);
                if (isCurrentMonth && d > todayObj) break;

                const diffToSat = (d.getDay() + 1) % 7; 
                const weekStart = new Date(d);
                weekStart.setDate(d.getDate() - diffToSat);
                weekStart.setHours(0,0,0,0);

                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                weekEnd.setHours(23,59,59,999);

                const jan1 = new Date(selectedYearForMonths, 0, 1);
                const jan1Diff = (jan1.getDay() + 1) % 7;
                const firstSat = new Date(jan1);
                firstSat.setDate(jan1.getDate() - jan1Diff);
                firstSat.setHours(0,0,0,0);
                
                const weekNum = Math.floor((weekStart - firstSat) / (7 * 24 * 60 * 60 * 1000)) + 1;
                const weekKey = weekStart.getTime();

                if (!weeksMap.has(weekKey)) {
                    const formatD = (dateObj) => `${dateObj.getDate()} ${ALL_MONTHS[dateObj.getMonth()].substring(0,3)}`;
                    const isTransition = (weekStart.getMonth() === 11 && weekEnd.getMonth() === 0);
                    const finalLabel = isTransition ? "TRANSITION WEEK" : `WEEK ${weekNum}`;

                    weeksMap.set(weekKey, {
                        weekKey: weekKey,
                        weekLabel: finalLabel,
                        dateLabel: `${formatD(weekStart)} - ${formatD(weekEnd)}`,
                        day: 0, night: 0, total: 0, isCurrentWeek: false
                    });
                }

                const weekData = weeksMap.get(weekKey);
                
                const localOffset = d.getTimezoneOffset() * 60000;
                const dateStr = new Date(d.getTime() - localOffset).toISOString().split('T')[0];

                if (dateStr === todayObj.toLocaleDateString('fr-CA')) {
                    weekData.isCurrentWeek = true;
                }

                const r = monthData[dateStr];
                if (r) {
                    weekData.day += r.Day || 0;
                    weekData.night += r.Night || 0;
                    weekData.total += r.Total || 0;
                }
            }

            Array.from(weeksMap.values()).forEach(w => {
                resultRows.push({
                    isWeekRow: true, 
                    weekVal: w.weekKey,
                    label: w.weekLabel,
                    subLabel: w.dateLabel,
                    day: w.day,
                    night: w.night,
                    total: w.total,
                    isCurrent: w.isCurrentWeek
                });
            });
        } else if (selectedYearForMonths) {
            const yearMonths = reports.month_wise[selectedYearForMonths] || {};
            const availableMonths = [];
            for(let i=0; i<12; i++) {
                if (selectedYearForMonths === currentYearStr && i > todayObj.getMonth()) break;
                availableMonths.push(ALL_MONTHS[i]);
            }
            availableMonths.forEach(monthName => {
                const r = yearMonths[monthName] || { Day: 0, Night: 0, Total: 0 };
                resultRows.push({ isMonthRow: true, monthVal: monthName, label: monthName, subLabel: selectedYearForMonths, day: r.Day, night: r.Night, total: r.Total, isCurrent: selectedYearForMonths === currentYearStr && monthName === currentMonthStr });
            });
        } else {
            const availableYears = Object.keys(reports.year_wise).sort((a,b) => a-b);
            if (!availableYears.includes(currentYearStr)) availableYears.push(currentYearStr);
            
            availableYears.forEach(yearName => {
                const r = reports.year_wise[yearName] || { Day: 0, Night: 0, Total: 0 };
                resultRows.push({ isYearRow: true, yearVal: yearName, label: yearName, subLabel: "Yearly Record", day: r.Day, night: r.Night, total: r.Total, isCurrent: yearName === currentYearStr });
            });
        }
    }
   
    return resultRows;
  }, [period, workerFilteredData, selectedYearForMonths, selectedMonthForDays, currentMonthTab]);

const handleBack = () => { 
      if(window.speechSynthesis) window.speechSynthesis.cancel();
      if (selectedDayForLooms) setSelectedDayForLooms(null); // 🌟 NAYA: Loom view se bahar aane ke liye
      else if (selectedWeekForDays) setSelectedWeekForDays(null); 
      else if (selectedMonthForDays) setSelectedMonthForDays(null); 
      else if (selectedYearForMonths) setSelectedYearForMonths(null); 
  };
  const isBackVisible = selectedYearForMonths !== null || selectedDayForLooms !== null;

 // 🌟 NAYA: Dynamic Strip Data Calculation for List Views 🌟
  const stripData = useMemo(() => {
      let totalProd = 0;
      let activeCount = 0;
      const activeLabel = "ACTIVE DAYS"; // हमेशा Active Days ही रहेगा
      
      // Total Production कैलकुलेट करें
      reportData.forEach(row => {
          if (parseFloat(row.total || 0) > 0) {
              totalProd += parseFloat(row.total);
          }
      });

      // 🌟 NAYA: Active Days की एग्ज़ैक्ट कैलकुलेशन (डेली रिकॉर्ड्स से)
      const dailyWise = workerFilteredData.reports?.daily_wise || {};
      
      if (selectedYearForMonths && !selectedMonthForDays && !selectedWeekForDays) {
          // Yearly View: पूरे साल के एक्टिव दिन गिनें
          const yearData = dailyWise[selectedYearForMonths] || {};
          Object.values(yearData).forEach(monthObj => {
              Object.values(monthObj).forEach(dayObj => {
                  if (parseFloat(dayObj.Total || 0) > 0) activeCount++;
              });
          });
      } else if (selectedYearForMonths && selectedMonthForDays && !selectedWeekForDays) {
          // Monthly View: पूरे महीने के एक्टिव दिन गिनें
          const monthData = dailyWise[selectedYearForMonths]?.[selectedMonthForDays] || {};
          Object.values(monthData).forEach(dayObj => {
              if (parseFloat(dayObj.Total || 0) > 0) activeCount++;
          });
      } else if (!selectedYearForMonths && !selectedMonthForDays && !selectedWeekForDays && period === 'total') {
          // Total View: सारे सालों के एक्टिव दिन गिनें
          Object.values(dailyWise).forEach(yearData => {
              Object.values(yearData).forEach(monthObj => {
                  Object.values(monthObj).forEach(dayObj => {
                      if (parseFloat(dayObj.Total || 0) > 0) activeCount++;
                  });
              });
          });
      } else {
          // Daily / Weekly Views
          reportData.forEach(row => {
              if (parseFloat(row.total || 0) > 0) activeCount++;
          });
      }

      const rate = parseFloat(myLiveApiData?.rate_per_meter || myLiveApiData?.ratePerMeter || 0);
      const totalEarn = totalProd * rate;

      return { totalProd, totalEarn, activeCount, activeLabel };
  }, [reportData, workerFilteredData, selectedYearForMonths, selectedMonthForDays, selectedWeekForDays, period, myLiveApiData]);

// 🌟 REACT PORTAL INJECTION TO FORCE OVERRIDE GLOBAL WORKER HEADER
  return createPortal(
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: appBg || '#f8fafc', zIndex: 999999, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        
     {/* 🌟 SOLID FORCE WHITE & TEXT FADE STYLE INJECTION 🌟 */}
        <style>{`
            body .forced-white-badge {
                color: #ffffff !important;
            }
            body .solid-white-production-box {
                background: #ffffff !important;
                backdrop-filter: none !important;
                -webkit-backdrop-filter: none !important;
            }
            body .forced-faded-prefix {
                font-size: 9px !important;
                color: #94A3B8 !important;
                opacity: 0.7 !important;
            }
            body .forced-small-fade-to {
                font-size: 9px !important;
                color: #94A3B8 !important;
                opacity: 0.5 !important;
                margin: 0 4px !important;
                display: inline-block !important;
            }
            body .forced-date-text {
                font-size: 9px !important;
                color: #64748B !important;
            }
            /* 🌟 NEW: NO PRODUCTION aur METER text ko safe fade tone dene ke liye */
            body .forced-faded-text {
                color: #94A3B8 !important;
                opacity: 0.65 !important;
            }
            /* 🌟 NEW: Day/Night ke production numbers ka size custom control karne ke liye */
            body .forced-day-night-num {
                font-size: 12px !important; 
                color: #334155 !important;
                font-weight: 700 !important;
            }
            /* 🌟 NEW: Total production number ko deep high-contrast dark color dene ke liye */
            body .forced-total-num {
                font-size: 14px !important;
                color: #000000 !important; 
                font-weight: 900 !important;
            }
        `}</style>

       {/* 🌟 MAIN UPDATED MASTER HEADER WITH BACK BUTTON & DYNAMIC CENTER VIEWS 🌟 */}
        <div style={{ background: '#ffffff', padding: '14px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', position:'sticky', top:0, zIndex:10, width: '100%', boxSizing: 'border-box', minHeight: '64px' }}>
            
            {/* ⬅️ BACK BUTTON ALWAYS ON THE LEFT (Safe Zone) */}
            <div style={{ position: 'relative', zIndex: 20 }}>
                <button onClick={(e) => handleVoiceClick ? handleVoiceClick(e, "रिपोर्ट से बैक जा रहे हैं।", isBackVisible ? handleBack : onClose) : (isBackVisible ? handleBack() : onClose())} style={{ background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', cursor: 'pointer', padding: '6px', display:'flex', alignItems:'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <ArrowLeft size={18} color="#0F172A" strokeWidth={2.5}/>
                </button>
            </div>

            {/* DYNAMIC HEADER CONTENT */}
            {selectedDayForLooms ? (
                // 🌟 Date/Day Center me Premium Arrow Buttons ke sath 🌟
                <div style={{ position: 'absolute', left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', zIndex: 10 }}>
                    <button 
                        onClick={() => {
                            const currentIndex = reportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate);
                            if (currentIndex > 0) setSelectedDayForLooms(reportData[currentIndex - 1]);
                        }}
                        style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.04)', opacity: reportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) > 0 ? 1 : 0.3, transition: 'all 0.2s' }}
                        disabled={reportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) <= 0}
                    >
                        <ChevronLeft size={18} color="#0F172A" strokeWidth={2.5} />
                    </button>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', minWidth: '120px' }}>
                        <h1 style={{ fontSize: '15px', fontWeight: '900', color: '#0F172A', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase', textAlign: 'center' }}>
                            {selectedDayForLooms.label}
                        </h1>
                        <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', textAlign: 'center' }}>
                            {selectedDayForLooms.subLabel}
                        </span>
                    </div>

                    <button 
                        onClick={() => {
                            const currentIndex = reportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate);
                            if (currentIndex !== -1 && currentIndex < reportData.length - 1) setSelectedDayForLooms(reportData[currentIndex + 1]);
                        }}
                        style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.04)', opacity: reportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) !== -1 && reportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) < reportData.length - 1 ? 1 : 0.3, transition: 'all 0.2s' }}
                        disabled={reportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) === -1 || reportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) >= reportData.length - 1}
                    >
                        <ChevronRight size={18} color="#0F172A" strokeWidth={2.5} />
                    </button>
                </div>
          ) : selectedWeekForDays ? (
                // 🌟 NAYA: Weekly View Center me Arrow Buttons (With Bounds, No Skips) 🌟
                <div style={{ position: 'absolute', left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', zIndex: 10 }}>
                    {(() => {
                        const todayNow = new Date();
                        
                        // 1. DOJ Date calculate karein (Lower Limit)
                        let dojDate = new Date(todayNow);
                        if (typeof myLiveApiData !== 'undefined' && myLiveApiData?.doj && myLiveApiData.doj !== '0000-00-00') {
                            dojDate = new Date(String(myLiveApiData.doj).replace(/-/g, '/'));
                        } else {
                            const dailyWise = (typeof workerFilteredData !== 'undefined' ? workerFilteredData.reports?.daily_wise : data?.reports?.daily_wise) || {};
                            let oldestDateStr = null;
                            Object.values(dailyWise).forEach(yearData => Object.values(yearData).forEach(monthData => Object.keys(monthData).forEach(dateStr => {
                                if (!oldestDateStr || dateStr < oldestDateStr) oldestDateStr = dateStr;
                            })));
                            if (oldestDateStr) dojDate = new Date(oldestDateStr);
                        }

                        // 2. Week Bounds calculate karein (Saturday start)
                        const getWeekStart = (dateObj) => {
                            const d = new Date(dateObj);
                            d.setHours(0,0,0,0);
                            const diffToSat = (d.getDay() + 1) % 7;
                            d.setDate(d.getDate() - diffToSat);
                            return d.getTime();
                        };

                        const dojWeekStartTime = getWeekStart(dojDate);
                        const currWeekStartTime = getWeekStart(todayNow);
                        const selectedStartTime = getWeekStart(new Date(selectedWeekForDays));

                        // 3. UI Labels ke liye calculation
                        const wStart = new Date(selectedStartTime);
                        const wEnd = new Date(wStart);
                        wEnd.setDate(wStart.getDate() + 6);
                        
                        const mths = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
                        const jan1 = new Date(selectedYearForMonths || wStart.getFullYear(), 0, 1);
                        const jan1Diff = (jan1.getDay() + 1) % 7;
                        const firstSat = new Date(jan1);
                        firstSat.setDate(jan1.getDate() - jan1Diff);
                        firstSat.setHours(0,0,0,0);
                        
                        const weekNum = Math.floor((wStart - firstSat) / (7 * 24 * 60 * 60 * 1000)) + 1;
                        const isTransition = (wStart.getMonth() === 11 && wEnd.getMonth() === 0);
                        const weekLabel = isTransition ? "TRANSITION WEEK" : `WEEK ${weekNum}`;

                        // Limit Logic: Prev aur Next kab disable honge
                        const isPrevDisabled = (selectedStartTime - 7 * 24 * 60 * 60 * 1000) < dojWeekStartTime;
                        const isNextDisabled = (selectedStartTime + 7 * 24 * 60 * 60 * 1000) > currWeekStartTime;

                        return (
                            <>
                                {/* LEFT ARROW (PREV WEEK) */}
                                <button 
                                    onClick={() => {
                                        if (!isPrevDisabled) setSelectedWeekForDays(selectedStartTime - 7 * 24 * 60 * 60 * 1000);
                                    }}
                                    disabled={isPrevDisabled}
                                    style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.04)', opacity: isPrevDisabled ? 0.3 : 1, transition: 'all 0.2s' }}
                                >
                                    <ChevronLeft size={18} color="#0F172A" strokeWidth={2.5} />
                                </button>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', minWidth: '150px' }}>
                                    <h1 style={{ fontSize: '15px', fontWeight: '900', color: '#0F172A', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase', textAlign: 'center' }}>
                                        {weekLabel}
                                    </h1>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '2px' }}>
                                        <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '800', letterSpacing: '0.8px', textTransform: 'uppercase' }}>DAILY VIEW</span>
                                        <span style={{ fontSize: '9px', color: '#475569', fontWeight: '700', background: '#F8FAFC', padding: '4px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            <CalendarDays size={10} color="#64748B" /> 
                                            {wStart.getDate()} {mths[wStart.getMonth()]} 
                                            <span style={{ fontSize: '9px', opacity: 0.35, margin: '0 2px', fontWeight: '500' }}>TO</span> 
                                            {wEnd.getDate()} {mths[wEnd.getMonth()]}
                                        </span>
                                    </div>
                                </div>

                                {/* RIGHT ARROW (NEXT WEEK) */}
                                <button 
                                    onClick={() => {
                                        if (!isNextDisabled) setSelectedWeekForDays(selectedStartTime + 7 * 24 * 60 * 60 * 1000);
                                    }}
                                    disabled={isNextDisabled}
                                    style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.04)', opacity: isNextDisabled ? 0.3 : 1, transition: 'all 0.2s' }}
                                >
                                    <ChevronRight size={18} color="#0F172A" strokeWidth={2.5} />
                                </button>
                            </>
                        );
                    })()}
                </div>
            ) : selectedMonthForDays ? (
                // 🌟 NAYA: Monthly View Center me Arrow Buttons (Only Valid Production Months + Bounds) 🌟
                <div style={{ position: 'absolute', left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', zIndex: 10 }}>
                    {(() => {
                        // 1. Data se un mahino (months) ki list nikalein jinme actual production hua hai
                        const targetReports = (typeof workerFilteredData !== 'undefined' ? workerFilteredData.reports : data?.reports) || {};
                        const validPairs = new Set();

                        if (targetReports?.month_wise) {
                            Object.entries(targetReports.month_wise).forEach(([y, monthsObj]) => {
                                Object.keys(monthsObj).forEach(m => {
                                    validPairs.add(`${m} ${y}`);
                                });
                            });
                        }

                        // 2. Current Date Limit (Upper Bound) set karein
                        const today = new Date();
                        const currMonth = ALL_MONTHS[today.getMonth()];
                        const currYear = today.getFullYear().toString();
                        validPairs.add(`${currMonth} ${currYear}`); // Ensure upper bound exists

                        // 3. Date of Joining (DOJ) Limit (Lower Bound) set karein
                        let dojDate = today;
                        if (typeof myLiveApiData !== 'undefined' && myLiveApiData?.doj && myLiveApiData.doj !== '0000-00-00') {
                            dojDate = new Date(String(myLiveApiData.doj).replace(/-/g, '/'));
                        } else if (validPairs.size > 1) {
                            // Agar DOJ na mile to available data me se sabse purana data uthayein
                            const allAbs = Array.from(validPairs).map(p => {
                                const [m, y] = p.split(' ');
                                return parseInt(y) * 12 + ALL_MONTHS.indexOf(m);
                            });
                            const minAbs = Math.min(...allAbs);
                            const minYear = Math.floor(minAbs / 12);
                            const minMonth = ALL_MONTHS[minAbs % 12];
                            if (!isNaN(minYear) && minMonth) {
                                dojDate = new Date(minYear, ALL_MONTHS.indexOf(minMonth), 1);
                            }
                        }
                        
                        const dojMonth = ALL_MONTHS[dojDate.getMonth()];
                        const dojYear = dojDate.getFullYear().toString();
                        validPairs.add(`${dojMonth} ${dojYear}`); // Ensure lower bound exists

                        // Helper function: Month & Year ko sortable number me convert karne ke liye
                        const toAbsMonth = (mStr, yStr) => parseInt(yStr) * 12 + ALL_MONTHS.indexOf(mStr);
                        const dojAbs = toAbsMonth(dojMonth, dojYear);
                        const currAbs = toAbsMonth(currMonth, currYear);

                        // 4. Array ko Filter (DOJ se Current Month tak) aur properly sort karein
                        const sortedValidList = Array.from(validPairs)
                            .map(pair => {
                                const [m, y] = pair.split(' ');
                                return { month: m, year: y, abs: toAbsMonth(m, y) };
                            })
                            .filter(item => item.abs >= dojAbs && item.abs <= currAbs)
                            .sort((a, b) => a.abs - b.abs);

                        // Abhi jo mahina screen par open hai uska index find karein
                        const currentAbs = toAbsMonth(selectedMonthForDays, selectedYearForMonths);
                        const currentIndex = sortedValidList.findIndex(item => item.abs === currentAbs);

                        return (
                            <>
                                {/* LEFT ARROW (PREV MONTH) */}
                                <button 
                                    onClick={() => {
                                        if (currentIndex > 0) {
                                            const prev = sortedValidList[currentIndex - 1];
                                            setSelectedMonthForDays(prev.month);
                                            setSelectedYearForMonths(prev.year);
                                        }
                                    }}
                                    disabled={currentIndex <= 0}
                                    style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.04)', opacity: currentIndex <= 0 ? 0.3 : 1, transition: 'all 0.2s' }}
                                >
                                    <ChevronLeft size={18} color="#0F172A" strokeWidth={2.5} />
                                </button>

                                {/* CENTER MONTH & YEAR TEXT */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', minWidth: '150px' }}>
                                    <h1 style={{ fontSize: '15px', fontWeight: '900', color: '#0F172A', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase', textAlign: 'center' }}>
                                        {`${selectedMonthForDays} ${selectedYearForMonths}`}
                                    </h1>
                                    <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '800', letterSpacing: '0.8px', textTransform: 'uppercase', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                        WEEKLY VIEW
                                    </span>
                                </div>

                                {/* RIGHT ARROW (NEXT MONTH) */}
                                <button 
                                    onClick={() => {
                                        if (currentIndex !== -1 && currentIndex < sortedValidList.length - 1) {
                                            const nxt = sortedValidList[currentIndex + 1];
                                            setSelectedMonthForDays(nxt.month);
                                            setSelectedYearForMonths(nxt.year);
                                        }
                                    }}
                                    disabled={currentIndex === -1 || currentIndex >= sortedValidList.length - 1}
                                    style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.04)', opacity: (currentIndex === -1 || currentIndex >= sortedValidList.length - 1) ? 0.3 : 1, transition: 'all 0.2s' }}
                                >
                                    <ChevronRight size={18} color="#0F172A" strokeWidth={2.5} />
                                </button>
                            </>
                        );
                    })()}
                </div>
            ) : selectedYearForMonths ? (
                // 🌟 NAYA: Yearly View Center me Arrow Buttons (Only Valid Production Years + Bounds) 🌟
                <div style={{ position: 'absolute', left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', zIndex: 10 }}>
                    {(() => {
                        // 1. Data se un saalon (years) ki list nikalein jinme actual production hua hai
                        const targetReports = (typeof workerFilteredData !== 'undefined' ? workerFilteredData.reports : data?.reports) || {};
                        const prodYears = Object.keys(targetReports?.year_wise || {}).map(Number);
                        
                        // 2. Current Year aur DOJ Year calculate karein (Bounds set karne ke liye)
                        const currY = new Date().getFullYear();
                        let dojY = currY; // Default fallback
                        if (typeof myLiveApiData !== 'undefined' && myLiveApiData?.doj && myLiveApiData.doj !== '0000-00-00') {
                            dojY = new Date(String(myLiveApiData.doj).replace(/-/g, '/')).getFullYear();
                        } else if (prodYears.length > 0) {
                            dojY = Math.min(...prodYears);
                        }

                        // 3. DOJ aur Current year ko array me daalein (taaki bounds hamesha accessible rahein)
                        if (!prodYears.includes(currY)) prodYears.push(currY);
                        if (!prodYears.includes(dojY)) prodYears.push(dojY);
                        
                        // 4. Array ko sort karein aur sirf (DOJ se Current Year) tak ki range rakhein
                        const validYears = prodYears
                            .sort((a, b) => a - b)
                            .filter(y => y >= dojY && y <= currY);

                        const currentSelectedNum = parseInt(selectedYearForMonths);
                        const currentIndex = validYears.indexOf(currentSelectedNum);

                        return (
                            <>
                                {/* LEFT ARROW (PREV YEAR) */}
                                <button 
                                    onClick={() => {
                                        if (currentIndex > 0) {
                                            setSelectedYearForMonths(validYears[currentIndex - 1].toString());
                                        }
                                    }}
                                    disabled={currentIndex <= 0}
                                    style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.04)', opacity: currentIndex <= 0 ? 0.3 : 1, transition: 'all 0.2s' }}
                                >
                                    <ChevronLeft size={18} color="#0F172A" strokeWidth={2.5} />
                                </button>

                                {/* CENTER YEAR TEXT */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', minWidth: '150px' }}>
                                    <h1 style={{ fontSize: '15px', fontWeight: '900', color: '#0F172A', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase', textAlign: 'center' }}>
                                        {`MONTHLY DATA (${selectedYearForMonths})`}
                                    </h1>
                                </div>

                                {/* RIGHT ARROW (NEXT YEAR) */}
                                <button 
                                    onClick={() => {
                                        if (currentIndex !== -1 && currentIndex < validYears.length - 1) {
                                            setSelectedYearForMonths(validYears[currentIndex + 1].toString());
                                        }
                                    }}
                                    disabled={currentIndex === -1 || currentIndex >= validYears.length - 1}
                                    style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(15,23,42,0.04)', opacity: (currentIndex === -1 || currentIndex >= validYears.length - 1) ? 0.3 : 1, transition: 'all 0.2s' }}
                                >
                                    <ChevronRight size={18} color="#0F172A" strokeWidth={2.5} />
                                </button>
                            </>
                        );
                    })()}
                </div>
            ) : (
                // 🌟 ORIGINAL LIST HEADER VIEW for Default (Total Production)
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginLeft: '14px', zIndex: 20 }}>
                    <h1 style={{ fontSize: '15px', fontWeight: '900', color: '#0F172A', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase' }}>
                        {title || "MY PRODUCTION HISTORY"}
                    </h1>

                    {!isBackVisible && (() => {
                        const todayB = new Date();
                        const sWeek = new Date(todayB); sWeek.setDate(todayB.getDate() - ((todayB.getDay() + 1) % 7));
                        const eWeek = new Date(sWeek); eWeek.setDate(sWeek.getDate() + 6);
                        
                        const sMonth = new Date(todayB.getFullYear(), todayB.getMonth(), 1);
                        const eMonth = new Date(todayB.getFullYear(), todayB.getMonth() + 1, 0);

                        let oDate = "1 JANUARY 2026";
                        if (data?.reports?.daily_wise) {
                            let dArr = [];
                            Object.values(data.reports.daily_wise).forEach(y => Object.values(y).forEach(m => Object.keys(m).forEach(d => dArr.push(d))));
                            if (dArr.length > 0) { dArr.sort(); oDate = new Date(dArr[0]).toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'}); }
                        }

                        let prefixText = "TOTAL";
                        let dateStartText = oDate;
                        let dateEndText = "Till Now";

                        if (period === 'week') {
                            prefixText = "THIS WEEK";
                            dateStartText = sWeek.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'});
                            dateEndText = eWeek.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'});
                        } else if (period === 'month') {
                            prefixText = "THIS MONTH";
                            dateStartText = sMonth.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'});
                            dateEndText = eMonth.toLocaleDateString('en-IN', {day:'numeric', month:'long', year:'numeric'});
                        }

                       return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                <span style={{ fontSize: '9px', color: '#94A3B8', fontWeight: '800', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                    {prefixText}
                                </span>
                                <span style={{ fontSize: '9px', color: '#475569', fontWeight: '700', background: '#F8FAFC', padding: '4px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <CalendarDays size={10} color="#64748B" />
                                    {dateStartText} 
                                    <span style={{ fontSize: '9px', opacity: 0.35, margin: '0 2px', fontWeight: '500' }}>TO</span> 
                                    {dateEndText}
                                </span>
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>

    {/* FULL HEIGHT CONTAINER (Scrolling moved to inner elements for sticky header) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px', boxSizing: 'border-box', minHeight: 0 }}>
            {/* 🛑 DUPICATE STRIP REMOVED: Yahan table ke upar se purana container complete delete ho gya h */}

           <div className="animate-in" style={{ flex: 1, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', boxShadow: '0 12px 35px rgba(0,0,0,0.05)', overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {selectedDayForLooms ? (
                    <div style={{ padding: '16px', overflowY: 'auto', flex: 1 }}>
                        {/* Duty and Rate Strips */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                            <div style={{ flex: 1, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Clock size={14} color="#64748B" />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{ fontSize: '7px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px' }}>DUTY TIME</span>
                                    <span style={{ fontSize: '10px', fontWeight: '800', color: '#0F172A', display: 'flex', alignItems: 'center' }}>
                                        {formatTimeForDisplay(myLiveApiData?.shift_start)} <span style={{ color: '#94A3B8', fontSize: '8px', margin: '0 2px' }}>TO</span> {formatTimeForDisplay(myLiveApiData?.shift_end)}
                                        {/* 🌟 NAYA: Duty Time Duration Chhota kar diya gaya */}
                                        {(() => {
                                            const s = parseTimeStr(myLiveApiData?.shift_start);
                                            const e = parseTimeStr(myLiveApiData?.shift_end);
                                            if(!s || !e) return null;
                                            let diff = e - s;
                                            if (diff < 0) diff += 1440;
                                            const h = Math.floor(diff / 60);
                                            const m = diff % 60;
                                            const dur = m > 0 ? `${h}:${String(m).padStart(2,'0')} HOURS` : `${h} HOURS`;
                                            return <><span style={{ color: '#94A3B8', margin: '0 4px', fontSize: '10px' }}>•</span><span style={{ color: '#64748B', fontSize: '10px', fontWeight: '800' }}>{dur}</span></>;
                                        })()}
                                    </span>
                                </div>
                            </div>
                            <div style={{ flex: 1, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <IndianRupee size={14} color="#64748B" />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{ fontSize: '7px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px' }}>RATE / METER</span>
                                    <span style={{ fontSize: '10px', fontWeight: '800', color: '#0F172A' }}>
                                        ₹{myLiveApiData?.rate_per_meter || myLiveApiData?.ratePerMeter || 0}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Looms Grid (Matches Workers.js Design) */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
                            {Object.entries(selectedDayForLooms.looms).map(([lId, meters]) => {
                                if(parseFloat(meters) <= 0) return null;
                                const loomIdLabel = String(lId).padStart(2, '0');
                                const isNight = workerShift === 'Night';
                                return (
                                    <div key={lId} style={{ background: '#FFFFFF', borderRadius: '16px', padding: '6px 2px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '8px', alignItems: 'center', padding: '0 4px' }}>
                                            <span style={{ fontSize: '9px', fontWeight: '900', color: '#94A3B8', letterSpacing: '0.2px' }}>LOOM {loomIdLabel}</span>
                                            <span style={{ fontSize: '6px', fontWeight: '800', color: '#64748B', background: '#F1F5F9', padding: '2px 4px', borderRadius: '4px', letterSpacing: '0.5px' }}>OFF</span>
                                        </div>
                                        <div style={{ width: '100%', background: isNight ? '#0F172A' : '#F8FAFC', border: isNight ? '1px solid #1E293B' : '1px solid #E2E8F0', borderRadius: '8px', padding: '6px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <span style={{ fontSize: '6px', fontWeight: '800', color: isNight ? '#94A3B8' : '#64748B', letterSpacing: '0.5px', marginBottom: '4px' }}>{isNight ? 'NIGHT SHIFT' : 'DAY SHIFT'}</span>
                                            <span style={{ fontSize: '13px', fontWeight: '900', color: isNight ? '#FFFFFF' : '#0F172A' }}>{parseFloat(meters).toFixed(1)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                       {/* 🌟 NAYA: Total Production & Earning - Ek Lamba Premium Box (Size +1px) */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '12px 14px', marginTop: '4px' }}>
                            
                            {/* Left Side: Production */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ background: '#E0F2FE', padding: '6px', borderRadius: '8px' }}>
                                    <Layers size={17} color="#0284C7" />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <span style={{ fontSize: '8px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px' }}>TOTAL PRODUCTION</span>
                                    <span style={{ fontSize: '15px', fontWeight: '900', color: '#0F172A' }}>
                                        {parseFloat(selectedDayForLooms.total || 0).toFixed(1)} <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700' }}>METER</span>
                                    </span>
                                </div>
                            </div>

                            {/* Center Line Divider */}
                            <div style={{ height: '30px', width: '1px', background: '#E2E8F0' }}></div>

                            {/* Right Side: Earning */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'right' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' }}>
                                    <span style={{ fontSize: '8px', fontWeight: '800', color: '#059669', letterSpacing: '0.5px' }}>TOTAL EARNING</span>
                                    <span style={{ fontSize: '15px', fontWeight: '900', color: '#10B981' }}>
                                        ₹{(parseFloat(selectedDayForLooms.total || 0) * parseFloat(myLiveApiData?.rate_per_meter || myLiveApiData?.ratePerMeter || 0)).toFixed(0)}
                                    </span>
                                </div>
                                <div style={{ background: '#D1FAE5', padding: '6px', borderRadius: '8px' }}>
                                    <IndianRupee size={17} color="#059669" />
                                </div>
                           </div>
                        </div>
                    </div>
               ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                        
                        {/* 🌟 NAYA: DYNAMIC DETAILS STRIP (LOCKED/STICKY POSITION) 🌟 */}
                        <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#ffffff', zIndex: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', padding: '14px', boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.8), 0 2px 8px rgba(0,0,0,0.02)' }}>
                                
                                {/* Top Stats Row */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ background: '#E0F2FE', padding: '8px', borderRadius: '10px' }}><Layers size={16} color="#0284C7" /></div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <span style={{ fontSize: '8px', fontWeight: '800', color: '#64748B', letterSpacing: '0.5px' }}>TOTAL PROD.</span>
                                            <span style={{ fontSize: '14px', fontWeight: '900', color: '#0F172A' }}>{stripData.totalProd.toFixed(1)} <span style={{ fontSize: '8px', color: '#94A3B8' }}>M</span></span>
                                        </div>
                                    </div>

                                    <div style={{ width: '1px', background: '#E2E8F0', height: '28px' }}></div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ background: '#D1FAE5', padding: '8px', borderRadius: '10px' }}><IndianRupee size={16} color="#059669" /></div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <span style={{ fontSize: '8px', fontWeight: '800', color: '#059669', letterSpacing: '0.5px' }}>TOTAL EARNING</span>
                                            <span style={{ fontSize: '14px', fontWeight: '900', color: '#10B981' }}>₹{stripData.totalEarn.toFixed(0)}</span>
                                        </div>
                                    </div>

                                    <div style={{ width: '1px', background: '#E2E8F0', height: '28px' }}></div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ background: '#FEF3C7', padding: '8px', borderRadius: '10px' }}><Activity size={16} color="#D97706" /></div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <span style={{ fontSize: '8px', fontWeight: '800', color: '#D97706', letterSpacing: '0.5px' }}>{stripData.activeLabel}</span>
                                            <span style={{ fontSize: '14px', fontWeight: '900', color: '#B45309' }}>{stripData.activeCount}</span>
                                        </div>
                                    </div>

                                </div>

                             {/* Bottom Badli / Overtime Row */}
                                <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #CBD5E1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Clock size={12} color="#64748B" />
                                        <span style={{ fontSize: '9px', fontWeight: '800', color: '#64748B', letterSpacing: '0.5px' }}>OVERTIME / BADLI DUTY</span>
                                    </div>
                                    <span style={{ fontSize: '11px', fontWeight: '900', color: '#0F172A' }}>0.0 <span style={{ fontSize: '8px', color: '#94A3B8' }}>METER</span></span>
                                </div>

                            </div>
                        </div>

                       {/* 🌟 SCROLLABLE TABLE AREA 🌟 */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>
                            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'11px', whiteSpace: 'nowrap'}}>
                          <thead style={{position: 'sticky', top: 0, background: '#ffffff', zIndex: 1}}>
    <tr style={{textAlign:'left', color:'#64748B', textTransform:'uppercase', letterSpacing:'0.8px'}}>
        <th style={{padding:'16px 8px', borderBottom:'1px solid #e2e8f0', fontSize:'11px', fontWeight:'800', width: '40%'}}>Timeline</th>
        <th style={{padding:'16px 8px', borderBottom:'1px solid #e2e8f0', fontSize:'10px', fontWeight:'800', background: '#F8FAFC', width: '18%'}}>Day</th>
        <th style={{padding:'16px 8px', borderBottom:'1px solid #e2e8f0', fontSize:'10px', fontWeight:'800', background: '#F1F5F9', width: '18%'}}>Night</th>
        <th style={{padding:'16px 8px', textAlign:'right', borderBottom:'1px solid #e2e8f0', fontSize:'10px', fontWeight:'800', width: '26%'}}>Total</th>
    </tr>
</thead>
                            <tbody>
                            {reportData.length > 0 ? reportData.map((row, idx) => {
                                const hasProduction = parseFloat(row.total || 0) > 0;
                                let rowVoiceMsg = null;
                                if (row.isYearRow) {
                                    if (hasProduction) rowVoiceMsg = `साल ${row.yearVal} में आपका टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन हुआ है।`;
                                } else if (row.isMonthRow) {
                                    if (hasProduction) rowVoiceMsg = `${row.monthVal} महीने में आपका टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन हुआ है।`;
                                } else {
                                    const dayNameMap = { 'Sunday': ' इतवार', 'Monday': ' पीर', 'Tuesday': ' मंगल', 'Wednesday': ' बुध', 'Thursday': ' जुमेरात', 'Friday': ' जुम्मा', 'Saturday': ' शनिवार' };
                                    const hindiDay = dayNameMap[row.subLabel] || row.subLabel;
                                    if (hasProduction) rowVoiceMsg = `${row.label} को ${hindiDay} के दिन आपका टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन हुआ है।`;
                                }
                                
                                return (
                                <tr key={idx} ref={row.isCurrent ? currentRowRef : null} 
                                    onClick={(e) => { if (rowVoiceMsg && handleVoiceClick) { window.speechSynthesis.cancel(); handleVoiceClick(e, rowVoiceMsg, null); } }}
                                    style={{ borderBottom:'1px solid #f1f5f9', borderLeft: row.isCurrent ? '3px solid #10B981' : '3px solid transparent', background: 'transparent', cursor: rowVoiceMsg ? 'pointer' : 'default' }}>
                                 <td style={{padding:'14px 8px', paddingLeft: row.isCurrent ? '12px' : '8px'}}>
                                    
                                   {/* Label and Date Strip Row (Combined inline) */}
<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
    <div style={{fontWeight:'800', color: row.isCurrent ? '#10B981' : '#0F172A', fontSize:'12px', display: 'flex', alignItems: 'center', gap: '6px'}}>
        {row.label}
        {row.isCurrent && <span className="forced-white-badge" style={{fontSize:'8px', background:'#10B981', padding:'2px 4px', borderRadius:'4px', verticalAlign:'middle', letterSpacing:'0.5px'}}>CURRENT</span>}
    </div>
    
    {/* Date Strip now on the right side */}
    {row.isWeekRow && (
        <span style={{ fontSize: '8px', color: '#475569', fontWeight: '800', background: '#F8FAFC', padding: '3px 6px', borderRadius: '6px', border: '1px solid #E2E8F0', display: 'inline-flex', alignItems: 'center', gap: '4px', textTransform: 'uppercase' }}>
            <CalendarDays size={8} color="#64748B" />
            {row.subLabel.replace('-', ' TO ')}
        </span>
    )}
</div>

                                    {/* Buttons Row */}
                                    {row.isYearRow ? (
                                        <div onClick={(e) => { e.stopPropagation(); if(window.speechSynthesis) window.speechSynthesis.cancel(); setSelectedYearForMonths(row.yearVal); }} style={{fontSize:'9px', color:'#0F172A', cursor:'pointer', fontWeight:'700', marginTop:'6px', display:'inline-flex', alignItems:'center', gap:'4px', background:'#f8fafc', border: '1px solid #e2e8f0', padding:'6px 10px', borderRadius:'10px'}}>View Monthly <ArrowRightLeft size={10} /></div>
                                    ) : row.isMonthRow ? (
                                        <div onClick={(e) => { e.stopPropagation(); if(window.speechSynthesis) window.speechSynthesis.cancel(); setSelectedMonthForDays(row.monthVal); }} style={{fontSize:'9px', color:'#0F172A', cursor:'pointer', fontWeight:'700', marginTop:'6px', display:'inline-flex', alignItems:'center', gap:'4px', background:'#f8fafc', border: '1px solid #e2e8f0', padding:'6px 10px', borderRadius:'10px'}}>View Weekly <ArrowRightLeft size={10} /></div>
                                  ) : row.isWeekRow ? (
    <div onClick={(e) => { e.stopPropagation(); if(window.speechSynthesis) window.speechSynthesis.cancel(); setSelectedWeekForDays(row.weekVal); }} style={{fontSize:'8.5px', color:'#0F172A', cursor:'pointer', fontWeight:'800', marginTop:'6px', display:'inline-flex', alignItems:'center', gap:'4px', background:'#ffffff', border: '1px solid #e2e8f0', padding:'4px 8px', borderRadius:'6px', boxShadow:'0 1px 2px rgba(0,0,0,0.02)'}}>VIEW DAILY <ChevronRight size={11} strokeWidth={3} /></div>
) : (
                                        <div style={{fontSize:'9px', color:'#64748B', fontWeight:'600', marginTop:'4px'}}>{row.subLabel}</div>
                                    )}
                                  </td>
                                    
                                   {hasProduction ? (
                                        <>
                                            <td style={{padding:'14px 8px', background: '#F8FAFC'}}>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}><span className="forced-day-night-num">{parseFloat(row.day || 0).toFixed(1)}</span><span className="forced-faded-text" style={{fontSize:'8px', fontWeight:'600', marginTop:'2px'}}>Meter</span></div>
                                            </td>
                                            <td style={{padding:'14px 8px', background: '#F1F5F9'}}>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}><span className="forced-day-night-num">{parseFloat(row.night || 0).toFixed(1)}</span><span className="forced-faded-text" style={{fontSize:'8px', fontWeight:'600', marginTop:'2px'}}>Meter</span></div>
                                            </td>
                                           <td style={{padding:'14px 8px', textAlign:'right'}}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                                                    {/* Number aur Meter Text */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                        <span className="forced-total-num">{parseFloat(row.total || 0).toFixed(1)}</span>
                                                        <span className="forced-faded-text" style={{fontSize:'9px', fontWeight:'700', marginTop:'2px'}}>Meter</span>
                                                    </div>
                                                    
                                                    {/* 🌟 NAYA: VIEW BUTTON (ICON ONLY) ON THE RIGHT 🌟 */}
                                                    {!row.isYearRow && !row.isMonthRow && !row.isWeekRow && (
                                                        <div 
                                                            onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                if(window.speechSynthesis) window.speechSynthesis.cancel(); 
                                                                setSelectedDayForLooms(row); 
                                                            }} 
                                                            style={{cursor:'pointer', display:'flex', alignItems:'center', justifyContent: 'center', background:'#EFF6FF', border: '1px solid #BFDBFE', padding:'6px', borderRadius:'8px', transition: 'all 0.2s'}}
                                                        >
                                                            <ChevronRight size={14} color="#2563EB" strokeWidth={2.5} />
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <td colSpan="3" style={{padding:'14px 8px', textAlign:'center', verticalAlign:'middle', background: '#F8FAFC'}}><span className="forced-faded-text" style={{fontSize:'8px', fontWeight:'700'}}>NO PRODUCTION</span></td>
                                    )}
                                </tr>
                                )}) : (<tr><td colSpan="4" style={{padding:'40px', textAlign:'center', color:'#64748B', fontWeight:'600'}}>No Records Found</td></tr>)}
                            </tbody>
                        </table>
                    </div>
                    </div>
                )}
            </div>
            <div style={{height: '40px'}}></div>
        </div>
        
    </div>,
    document.body
  );
};

// --- MAIN WORKER HOME ---
export default function WorkerHome({ user, workersData, allLooms, statsTab, setStatsTab, onOpenProfile, stats, appBg, handleVoiceClick, voiceAssist, speakHinglish }) {
    const [loomTimeFilter, setLoomTimeFilter] = useState('today');
   const [statsModalData, setStatsModalData] = useState(null);
   const [isReportLoading, setIsReportLoading] = useState(false);
   
   // Loom History Database fetch setup
    const [loomHistory, setLoomHistory] = useState({});
    const [activeShift, setActiveShift] = useState('day');

    // 1. ROBUST LIVE DATA EXTRACT (Moved Up for dynamic shift logic)
    const myLiveApiData = workersData.find(w => 
        String(w.mobile).trim() === String(user.mobile).trim() || 
        String(w.id) === String(user.id)
    ) || user; 

    // Shift Tracking (Advanced Sync with specific Worker's or Owner's Time)
    useEffect(() => {
        const checkShift = () => {
            try {
                const nowStr = new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
                const nowIST = new Date(nowStr);
                const currentHour = nowIST.getHours();
                const currentMinute = nowIST.getMinutes();
                const currentMins = (currentHour * 60) + currentMinute;

                const parseTimeStr = (timeStr, defaultHour, defaultMinute = 0) => {
                    if (!timeStr || typeof timeStr !== 'string') return (defaultHour * 60) + defaultMinute;
                    const match = timeStr.trim().match(/(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)?/i);
                    if (match) {
                        let h = parseInt(match[1], 10);
                        let m = match[2] ? parseInt(match[2], 10) : 0;
                        const ampm = match[3] ? match[3].toUpperCase() : null;
                        if (ampm === 'PM' && h < 12) h += 12;
                        if (ampm === 'AM' && h === 12) h = 0;
                        return (h * 60) + m;
                    }
                    return (defaultHour * 60) + defaultMinute;
                };

                // Yahan specific worker ka shift_start / end fetch hoga
                // Agar null hai toh 8:00 AM / 8:00 PM fallback hoga
                const dayStartMins = parseTimeStr(myLiveApiData?.shift_start, 8, 0);   
                const nightStartMins = parseTimeStr(myLiveApiData?.shift_end, 20, 0); 
                
                let newShift = 'day';
                if (dayStartMins < nightStartMins) {
                    if (currentMins >= dayStartMins && currentMins < nightStartMins) newShift = 'day';
                    else newShift = 'night';
                } else {
                    if (currentMins >= dayStartMins || currentMins < nightStartMins) newShift = 'day';
                    else newShift = 'night';
                }
                setActiveShift(newShift);
            } catch (err) {
                console.error("Worker Shift Calculation Error:", err);
                setActiveShift('day');
            }
        };
        checkShift();
        const interval = setInterval(checkShift, 60000); 
        return () => clearInterval(interval);
    }, [myLiveApiData?.shift_start, myLiveApiData?.shift_end]);

    // --- REPLACE THIS IN WorkerHome useEffect (Loom History Fetch) ---
    useEffect(() => {
        const controller = new AbortController();
        const fetchLoomHistoryData = async () => {
            try {
                const res = await fetch(`${API_BASE}/get_loom_stats.php?t=${Date.now()}`, { signal: controller.signal });
                const data = await res.json();
                if (data.status === 'success') {
                    setLoomHistory(data.data);
                }
            } catch (e) {
                if (e.name !== 'AbortError') console.error("Fetch error:", e);
            }
        };
        fetchLoomHistoryData();
        const interval = setInterval(fetchLoomHistoryData, 3000); 
        return () => { clearInterval(interval); controller.abort(); };
    }, []);
    
    const today = new Date();

   // 2. STRICT EXACT ASSIGNED LOOMS FILTER (Fix for unassigned looms showing up)
    const getLoomArrayWorker = (loomStr) => {
        if (!loomStr) return [];
        return loomStr.toString().split(',')
            .map(s => s.replace(/[^0-9]/g, '').trim())
            .filter(s => s !== '');
    };
    
    // 🌟 NAYA LOGIC: Ab old 'user' (local storage cache) ka data ignore karke STRICTLY live API ('myLiveApiData') ka data use hoga
    const strictAssignedIds = new Set(getLoomArrayWorker(myLiveApiData?.looms));

    const myLooms = allLooms.filter(loom => {
        if (!loom) return false;
        const lId = String(loom.loom_id || loom.loom_no || loom.id || '').replace(/[^0-9]/g, '');
        return strictAssignedIds.has(lId);
    });

  const isAnyLoomActive = myLooms.some(loom => isLoomActive(loom.last_updated));

  // 🌟 JADH SE SAHI KARNE WALA TRUE LOGS ENGINE (METERS, EARNINGS & LOOMS UNIFIED) 🌟
    const [trueWorkerTotalMeters, setTrueWorkerTotalMeters] = useState(0);
    const [trueWorkerTotalEarnings, setTrueWorkerTotalEarnings] = useState(0);
    const [workerLoomStats, setWorkerLoomStats] = useState({}); // Per-loom dynamic breakdown

    useEffect(() => {
        if (!user?.id) return;
        
        fetch(`${API_BASE}/get_worker_history.php?id=${user.id}&t=${Date.now()}`)
            .then(res => res.json())
            .then(json => {
                if (json.status === 'success' && Array.isArray(json.data)) {
                    let totalMetersSum = 0;
                    let totalEarningsSum = 0;
                    let loomMap = {}; // Local accumulator for individual looms
                    
                    const todayObj = new Date();
                    const todayDateStr = todayObj.toLocaleDateString('fr-CA'); // Local YYYY-MM-DD Format

                    // Current Week boundary tracking (Saturday to Friday)
                    const currentDay = todayObj.getDay(); 
                    const diffToSat = (currentDay + 1) % 7; 
                    const startOfWeekObj = new Date(todayObj);
                    startOfWeekObj.setDate(todayObj.getDate() - diffToSat);
                    startOfWeekObj.setHours(0,0,0,0);
                    const endOfWeekObj = new Date(startOfWeekObj);
                    endOfWeekObj.setDate(startOfWeekObj.getDate() + 6);
                    endOfWeekObj.setHours(23,59,59,999);

                    let validDojObj = null;
                    if (user?.doj && user.doj !== '0000-00-00' && user.doj.trim() !== '') {
                        validDojObj = new Date(String(user.doj).replace(/-/g, '/'));
                        validDojObj.setHours(0, 0, 0, 0);
                    }
                    
                    json.data.forEach(e => {
                        const rawDate = e.date || e.entry_date || e.created_at || '';
                        if (!rawDate) return;
                        
                        const dateParts = rawDate.split(' ')[0];
                        const d = new Date(dateParts.replace(/-/g, '/'));
                        d.setHours(0, 0, 0, 0);
                        
                        if (validDojObj && d < validDojObj) return;
                        
                        const m = parseFloat(e.metersProduced || e.meters || e.total || e.production_meters || 0);
                        totalMetersSum += m;
                        totalEarningsSum += parseFloat(e.earning || 0);

                        // Loom ID mapping filters
                        const lId = String(e.loom_id).replace(/[^0-9]/g, '');
                        if (!lId) return;

                        if (!loomMap[lId]) {
                            loomMap[lId] = { today: 0, week: 0, month: 0, total: 0 };
                        }

                        // Chronological breakdown calculation
                        loomMap[lId].total += m;
                        if (dateParts === todayDateStr) {
                            loomMap[lId].today += m;
                        }
                        if (d >= startOfWeekObj && d <= endOfWeekObj) {
                            loomMap[lId].week += m;
                        }
                        if (d.getFullYear() === todayObj.getFullYear() && d.getMonth() === todayObj.getMonth()) {
                            loomMap[lId].month += m;
                        }
                    });
                    
                    setTrueWorkerTotalMeters(totalMetersSum);
                    setTrueWorkerTotalEarnings(totalEarningsSum);
                    setWorkerLoomStats(loomMap);
                }
            })
            .catch(err => console.error("Error inside unified true logs engine:", err));
    }, [user.id, user.doj, workersData]);

// --- DATE CALCULATIONS ---
    const formatOpts = { day: 'numeric', month: 'long', year: 'numeric' };
    const formatOptsShort = { day: 'numeric', month: 'long' }; 
    const todayStr = today.toLocaleDateString('en-IN', formatOpts);
    
    const currentDay = today.getDay(); 
    const diffToSat = (currentDay + 1) % 7; 
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - diffToSat);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    const weekStr = `${startOfWeek.toLocaleDateString('en-IN', formatOpts)} - ${endOfWeek.toLocaleDateString('en-IN', formatOpts)}`;
    const weekToggleStr = `${startOfWeek.toLocaleDateString('en-IN', formatOptsShort)} - ${endOfWeek.toLocaleDateString('en-IN', formatOptsShort)}`;

    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const monthStr = `${startOfMonth.toLocaleDateString('en-IN', formatOpts)} - ${endOfMonth.toLocaleDateString('en-IN', formatOpts)}`;
    const monthToggleStr = `${startOfMonth.toLocaleDateString('en-IN', formatOptsShort)} - ${endOfMonth.toLocaleDateString('en-IN', formatOptsShort)}`;

// // 🌟 DYNAMIC START DATE (DOJ) CALCULATION 🌟
let oldestDateStr = today.toLocaleDateString('en-IN', formatOpts); 
let oldestDateStrShort = today.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

if (user && user.doj && user.doj.trim() !== '') {
     // Dash ko slash se badla taaki exact date browser screen par locked rahe
     const oldest = new Date(String(user.doj).replace(/-/g, '/')); 
     if (!isNaN(oldest)) {
             oldestDateStr = oldest.toLocaleDateString('en-IN', formatOpts);
             oldestDateStrShort = oldest.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
         }
    }
    
    const totalStr = `${oldestDateStr} - ${todayStr}`;
    const totalToggleStr = `${oldestDateStrShort} - Till Now`;
    const todayToggleStr = today.toLocaleDateString('en-IN', formatOptsShort);

// 🌟 PERFECT DATE SYNC LOGIC (DOJ & FILTER CONNECTED) 🌟
const activeDateStr = loomTimeFilter === 'today' ? todayStr : 
                      loomTimeFilter === 'week' ? weekStr : 
                      loomTimeFilter === 'month' ? monthStr : totalStr;

    // --- MATH & STATS ---
    const safeParse = (val) => isNaN(parseFloat(val)) ? 0 : parseFloat(val);

    const getStatsValue = () => {
        if (loomTimeFilter === 'today') return safeParse(myLiveApiData.today);
        if (loomTimeFilter === 'week') return safeParse(myLiveApiData.weekly);
        if (loomTimeFilter === 'month') return safeParse(myLiveApiData.monthly);
        if (loomTimeFilter === 'total') return safeParse(myLiveApiData.yearly);
        return 0;
    };

    const activeStats = {
        Total: getStatsValue(),
        Day: myLiveApiData.shift === 'Day' ? getStatsValue() : 0,
        Night: myLiveApiData.shift === 'Night' ? getStatsValue() : 0,
    };

    const todayStats = {
        total: safeParse(myLiveApiData.today),
        day: myLiveApiData.shift === 'Day' ? safeParse(myLiveApiData.today) : 0,
        night: myLiveApiData.shift === 'Night' ? safeParse(myLiveApiData.today) : 0
    };

    const getTopActiveDays = () => {
        if (loomTimeFilter === 'today') return todayStats.total > 0 ? 1 : 0;
        if (loomTimeFilter === 'week') return stats?.summaries?.week?.ActiveDays || 0;
        if (loomTimeFilter === 'month') return stats?.summaries?.month?.ActiveDays || 0;
        if (loomTimeFilter === 'total') {
            const apiDays = stats?.summaries?.total?.ActiveDays || 0;
            const maxDays = calculateTotalDays(user.doj);
            return apiDays > maxDays ? maxDays : (apiDays === 0 ? maxDays : apiDays);
        }
        return 0;
    };
    const topActiveDays = getTopActiveDays();

    const getLoomActiveDays = () => {
        if (loomTimeFilter === 'today') return todayStats.total > 0 ? 1 : 0;
        if (loomTimeFilter === 'week') return stats?.summaries?.week?.ActiveDays || 0;
        if (loomTimeFilter === 'month') return stats?.summaries?.month?.ActiveDays || 0;
        if (loomTimeFilter === 'total') {
            const apiDays = stats?.summaries?.total?.ActiveDays || 0;
            const maxDays = calculateTotalDays(user.doj);
            return apiDays > maxDays ? maxDays : (apiDays === 0 ? maxDays : apiDays);
        }
        return 0;
    };
    const loomActiveDays = getLoomActiveDays();

    const filterData = {
        today: { label: 'Today', date: todayToggleStr },
        week: { label: 'This Week', date: weekToggleStr },
        month: { label: 'This Month', date: monthToggleStr },
        total: { label: 'Total', date: totalToggleStr }
    };

    // --- AI VOICE MESSAGES LOGIC (Dynamic 0 Production Check) ---
    const monthNamesVoice = [" जनवरी", " फरवरी", " मार्च", " अप्रैल", " मई", " जून", " जुलाई", " अगस्त", " सितंबर", " अक्टूबर", " नवंबर", " दिसंबर"];
    const formatVoiceDate = (dateObj) => {
        if (!dateObj || isNaN(dateObj.getTime())) return "";
        return `${dateObj.getDate()} ${monthNamesVoice[dateObj.getMonth()]}`;
    };

    const todayVoiceDate = `${today.getDate()} ${monthNamesVoice[today.getMonth()]} ${today.getFullYear()}`;
    

    const weekTotalVoice = safeParse(myLiveApiData.weekly);
    const monthTotalVoice = safeParse(myLiveApiData.monthly);
    const totalTotalVoice = safeParse(myLiveApiData.yearly);

    // PERIOD ACTIVE DAYS LOGIC
    const weekActiveDays = stats?.summaries?.week?.ActiveDays || 0;
    const monthActiveDays = stats?.summaries?.month?.ActiveDays || 0;
    const totalActiveDaysApi = stats?.summaries?.total?.ActiveDays || 0;
    
    // Naya logic: Voice ko joining date ke maximum days tak cap (limit) karna
    const maxWorkerDays = calculateTotalDays(user.doj);
    const cappedTotalDays = totalActiveDaysApi > maxWorkerDays ? maxWorkerDays : (totalActiveDaysApi === 0 ? maxWorkerDays : totalActiveDaysApi);
    const totalActiveDays = totalTotalVoice > 0 ? cappedTotalDays : 0;

    // --- PRE-CALCULATE EXACT SUMS FOR ASSIGNED LOOMS VOICE ---
    const workerShiftForCalc = myLiveApiData.shift === 'Night' ? 'night' : 'day';
    let calcTodayVoice = 0, calcWeekVoice = 0, calcMonthVoice = 0, calcTotalVoice = 0;

    // लूम कार्ड्स की एग्जैक्ट कैलकुलेशन ताकि स्क्रीन और आवाज़ 100% मैच हों
    myLooms.forEach((loom, idx) => {
        const loomIdLabel = String(loom.loom_id || loom.loom_no || loom.id || (idx+1)).replace(/[^0-9]/g, '');
        const baseMeters = parseFloat(loom.meters || 0);
        const foundObj = loomHistory[loomIdLabel] || loomHistory[String(loomIdLabel).padStart(2, '0')] || {};

        let todayDay = loom.day_meters > 0 ? loom.day_meters : (activeShift === 'day' ? baseMeters : 0);
        let todayNight = loom.night_meters > 0 ? loom.night_meters : (activeShift === 'night' ? baseMeters : 0);

        let wDay = parseFloat(foundObj.week_day || 0) + todayDay;
        let wNight = parseFloat(foundObj.week_night || 0) + todayNight;
        
        let mDay = parseFloat(foundObj.month_day || 0) + todayDay;
        let mNight = parseFloat(foundObj.month_night || 0) + todayNight;
        
        let tDay = parseFloat(foundObj.total_day || 0) + todayDay;
        let tNight = parseFloat(foundObj.total_night || 0) + todayNight;

        if (workerShiftForCalc === 'night') {
            calcTodayVoice += todayNight;
            calcWeekVoice += wNight;
            calcMonthVoice += mNight;
            calcTotalVoice += tNight;
        } else {
            calcTodayVoice += todayDay;
            calcWeekVoice += wDay;
            calcMonthVoice += mDay;
            calcTotalVoice += tDay;
        }
    });

     

    // ASSIGNED LOOMS TOGGLE VOICE MESSAGES (Perfect Match with UI)
   const loomVoiceMsgs = {
                today: todayStats.total > 0 
                    ? ` आज के दिन ${formatVoiceDate(today)} को आपका टोटल प्रोडक्शन ${todayStats.total.toFixed(1)} मीटर हुआ है, जिसे नीचे लूम के हिसाब से दिखाया गया है।`
                    : ` आज की तारीख ${todayVoiceDate} को आपका प्रोडक्शन जीरो मीटर है।`,
                week: safeParse(myLiveApiData.weekly) > 0
                    ? ` इस हफ्ते ${formatVoiceDate(startOfWeek)} से ${formatVoiceDate(endOfWeek)} तक, आपने टोटल ${weekActiveDays} दिन ड्यूटी कि है, और आपका टोटल प्रोडक्शन ${safeParse(myLiveApiData.weekly).toFixed(1)} मीटर हुआ है, जिसे नीचे लूम के हिसाब से दिखाया गया है।`
                    : ` इस हफ्ते ${formatVoiceDate(startOfWeek)} से ${formatVoiceDate(endOfWeek)} तक, आपने एक भी दिन ड्यूटी नहीं कि है इसलिए प्रोडक्शन जीरो मीटर है।`,
                month: safeParse(myLiveApiData.monthly) > 0
                    ? ` इस महीने ${formatVoiceDate(startOfMonth)} से ${formatVoiceDate(endOfMonth)} तक, आपने टोटल ${monthActiveDays} दिन ड्यूटी कि है, और आपका टोटल प्रोडक्शन ${safeParse(myLiveApiData.monthly).toFixed(1)} मीटर हुआ है, जिसे नीचे लूम के हिसाब से दिखाया गया है।`
                    : ` इस महीने ${formatVoiceDate(startOfMonth)} से ${formatVoiceDate(endOfMonth)} तक, आपने एक भी दिन ड्यूटी नहीं कि है इसलिए प्रोडक्शन जीरो मीटर है।`,
                total: totalTotalVoice > 0
                    ? ` टोटल ${oldestDateStrShort} से अब तक यानी ${formatVoiceDate(today)} तक, आपने टोटल ${totalActiveDays} दिन ड्यूटी कि है, और आपका टोटल प्रोडक्शन ${totalTotalVoice.toFixed(1)} मीटर हुआ है, जिसे नीचे लूम के हिसाब से दिखाया गया है।`
                    : ` टोटल प्रोडक्शन ${oldestDateStrShort} से ${formatVoiceDate(today)} तक, आपने एक भी दिन ड्यूटी नहीं कि है इसलिए प्रोडक्शन जीरो मीटर है।`
            };
    const getHeaderDateString = () => {
        switch(loomTimeFilter) { case 'week': return weekStr; case 'month': return monthStr; case 'total': return totalStr; case 'today': default: return todayStr; }
    };

   return (
        <div style={styles.content}>
            
           <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
                
                * { 
                  font-family: 'Plus Jakarta Sans', sans-serif; 
                  box-sizing: border-box; 
                  -webkit-tap-highlight-color: transparent; 
                  text-transform: uppercase !important;
                }

                ::-webkit-scrollbar { width: 4px; height: 4px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: rgba(15, 23, 42, 0.15); border-radius: 10px; }
                
                @keyframes slideUpFade { 0% { opacity: 0; transform: translateY(15px); } 100% { opacity: 1; transform: translateY(0); } }
                @keyframes pulseGlow { 0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); } 70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); } 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); } }
                
                .animate-in { animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                
                .premium-glass {
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(16px);
                    -webkit-backdrop-filter: blur(16px);
                    border: 1px solid rgba(255, 255, 255, 0.6);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,1);
                    border-radius: 24px;
                    background: window.innerWidth < 600 ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: window.innerWidth < 600 ? 'none' : 'blur(10px)',
                }
            `}</style>

            {/* ✅ DUPICATE STRIP REMOVED: Main home page se be-wajah ka second white header permanently delete ho gaya hai */}

           {/* 🌟 1000x ULTRA-PREMIUM MINIMALIST BANNERS (Platinum & Slate) 🌟 */}
            <div className="animate-in" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                
                {/* 1. MY SHIFT BOX */}
                <div style={{ 
                    background: '#FFFFFF', 
                    padding: '10px 12px', 
                    borderRadius: '16px', 
                    display: 'flex', alignItems: 'center', gap: '12px', 
                    border: '1px solid #F1F5F9',
                    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.03), 0 1px 3px rgba(15, 23, 42, 0.02)'
                }}>
                    <div style={{ 
                        background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', 
                        padding: '8px', 
                        borderRadius: '10px', 
                        border: '1px solid #E2E8F0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <Clock size={16} color="#334155" strokeWidth={2.5} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        <span style={{ fontSize: '9px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                            {myLiveApiData?.pending_shift_start ? 'Next Shift' : 'My Shift'}
                        </span>
                        
                        {/* 🌟 NEW: टाइम के बीच में हल्का और फेडेड 'TO' ऐड किया गया है 🌟 */}
                        <span style={{ fontSize: '13px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.2px', display: 'flex', alignItems: 'center' }}>
                            {myLiveApiData?.pending_shift_start ? (
                                <>
                                    {formatTimeForDisplay(myLiveApiData.pending_shift_start)}
                                    <span style={{ fontSize: '9px', color: '#94A3B8', fontWeight: '700', margin: '0 4px', opacity: 0.6 }}>TO</span>
                                    {formatTimeForDisplay(myLiveApiData.pending_shift_end)}
                                </>
                            ) : (myLiveApiData?.shift_start && myLiveApiData?.shift_end ? (
                                <>
                                    {formatTimeForDisplay(myLiveApiData.shift_start)}
                                    <span style={{ fontSize: '9px', color: '#94A3B8', fontWeight: '700', margin: '0 4px', opacity: 0.6 }}>TO</span>
                                    {formatTimeForDisplay(myLiveApiData.shift_end)}
                                </>
                            ) : 'Not Set')}
                        </span>
                        
                    </div>
                </div>

                {/* 2. RATE/METER BOX */}
                <div style={{ 
                    background: '#FFFFFF', 
                    padding: '10px 12px', 
                    borderRadius: '16px', 
                    display: 'flex', alignItems: 'center', gap: '12px', 
                    border: '1px solid #F1F5F9',
                    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.03), 0 1px 3px rgba(15, 23, 42, 0.02)'
                }}>
                    <div style={{ 
                        background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', 
                        padding: '8px', 
                        borderRadius: '10px', 
                        border: '1px solid #E2E8F0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <IndianRupee size={16} color="#334155" strokeWidth={2.5} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        <span style={{ fontSize: '9px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                            {myLiveApiData?.pending_rate > 0 ? 'New Rate' : 'Rate/Meter'}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.2px' }}>
                            ₹{String(myLiveApiData?.pending_rate > 0 ? myLiveApiData.pending_rate : (myLiveApiData?.rate_per_meter || myLiveApiData?.ratePerMeter || 0))}
                        </span>
                    </div>
                </div>
                
            </div>

         
           {/* ===== 🌟 1000x ULTRA-PREMIUM SOLID COMBINED CARD (Platinum & Slate) 🌟 ===== */}
            <div className="animate-in" style={{ 
                background: '#FFFFFF', 
                border: '1px solid #F1F5F9', 
                borderRadius: '24px', 
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.02)', 
                overflow: 'hidden' 
            }}>
                <div style={{ padding: '14px' }}>
                    
                    {/* 1. SINGLE SET OF TOGGLE BUTTONS (SOLID PLATINUM STYLE) */}
                    <div style={{ 
                        display: 'flex', 
                        background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)', 
                        padding: '6px', 
                        borderRadius: '16px', 
                        gap: '4px',
                        marginBottom: '16px', 
                        boxShadow: 'inset 0 2px 5px rgba(15,23,42,0.02)', 
                        border: '1px solid #E2E8F0',
                        overflowX: 'auto', 
                        scrollbarWidth: 'none',
                        alignItems: 'center' 
                    }}>
                        {Object.keys(filterData).map((filter) => {
                            const isActiveFilter = loomTimeFilter === filter;
                            
                            const introMsgs = {
                                today: " ये टुडे बटन है, इसमें आप आज का टोटल प्रोडक्शन देख सकते है, देखने के लिए दोबारा क्लिक करें।",
                                week: " ये दिस वीक बटन है, इसमें आप इस हफ्ते का टोटल प्रोडक्शन देख सकते है, देखने के लिए दोबारा क्लिक करें।",
                                month: " ये दिस मंथ बटन है, इसमें आप इस महीने का टोटल प्रोडक्शन देख सकते है, देखने के लिए दोबारा क्लिक करें।",
                                total: " ये टोटल बटन है, इसमें आप टोटल प्रोडक्शन देख सकते है, देखने के लिए दोबारा क्लिक करें।"
                            };

                            return (
                                <button 
                                    key={filter} 
                                    onClick={(e) => {
                                        handleVoiceClick && handleVoiceClick(
                                            e, 
                                            introMsgs[filter], 
                                            () => { 
                                                setLoomTimeFilter(filter); 
                                                if (setStatsTab) setStatsTab(filter === 'today' ? 'week' : filter); 
                                                if (voiceAssist && speakHinglish) speakHinglish(loomVoiceMsgs[filter]); 
                                            }, 
                                            filter === 'today' ? 'tab-stats-today' : `tab-stats-${filter}`,
                                            isActiveFilter, 
                                            loomVoiceMsgs[filter] 
                                        );
                                    }} 
                                    style={isActiveFilter ? {
                                        flex: 1.2, 
                                        minWidth: '75px', 
                                        padding: '10px 4px', 
                                        background: '#FFFFFF', 
                                        border: '1px solid #E2E8F0', 
                                        borderRadius: '12px', 
                                        fontWeight: '700', 
                                        color: '#0F172A', 
                                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)', 
                                        cursor: 'pointer', 
                                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        animation: 'bubblePop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                                    } : {
                                        flex: 1, 
                                        minWidth: '60px', 
                                        padding: '6px 4px', 
                                        background: 'transparent', 
                                        border: '1px solid transparent', 
                                        borderRadius: '12px', 
                                        fontWeight: '600', 
                                        color: '#64748B', 
                                        cursor: 'pointer', 
                                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        transform: 'scale(0.92)'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 0' }}>
                                        <div style={{ 
                                            fontSize: isActiveFilter ? '10px' : '9px', 
                                            fontWeight: '800', 
                                            textTransform: 'uppercase', 
                                            letterSpacing: '0.5px', 
                                            color: isActiveFilter ? '#0F172A' : '#64748B',
                                            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                                        }}>
                                            {filter === 'week' ? 'THIS WEEK' : filter === 'month' ? 'THIS MONTH' : filterData[filter].label.toUpperCase()}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* 2. DYNAMIC TOTAL PRODUCTION SUMMARY (Solid Premium Look) */}
                    {(() => {
                        let currentTotal = 0, currentActiveDays = 0;
                        if (loomTimeFilter === 'today') { 
                            currentTotal = todayStats.total; 
                            currentActiveDays = todayStats.total > 0 ? 1 : 0; 
                        } else if (loomTimeFilter === 'week') { 
                            currentTotal = safeParse(myLiveApiData.weekly); 
                            currentActiveDays = stats?.summaries?.week?.ActiveDays || 0; 
                        } else if (loomTimeFilter === 'month') { 
                            currentTotal = safeParse(myLiveApiData.monthly); 
                            currentActiveDays = stats?.summaries?.month?.ActiveDays || 0; 
                        } else if (loomTimeFilter === 'total') { 
                            currentTotal = safeParse(trueWorkerTotalMeters); 
                            currentActiveDays = topActiveDays; 
                        }
                        
                        let productionLabel = "TOTAL PRODUCTION";
                        if (loomTimeFilter === 'today') productionLabel = "TODAY'S PRODUCTION";
                        else if (loomTimeFilter === 'week') productionLabel = "THIS WEEK'S PRODUCTION";
                        else if (loomTimeFilter === 'month') productionLabel = "THIS MONTH'S PRODUCTION";
                        
                        return (
                            <div onClick={(e) => handleVoiceClick && handleVoiceClick(e, loomVoiceMsgs[loomTimeFilter], null)}>
                                
                                {/* 🌟 FULL WIDTH PRODUCTION CARD WITH INTEGRATED DATE & ACTIVE DAYS STRIP 🌟 */}
                                <div style={{ width: '100%', marginBottom: '12px' }}>
                                    <div style={{ 
                                        background: '#FFFFFF', 
                                        borderRadius: '16px', 
                                        padding: '14px 16px', 
                                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)', 
                                        border: '1px solid #E2E8F0', 
                                        display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' 
                                    }}>
                                        
                                        {/* Card Header: Icon, Label & Embedded Date Strip */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ background: 'linear-gradient(135deg, #E0F2FE, #7DD3FC)', padding: '8px', borderRadius: '10px', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.15)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Layers size={16} color="#0284C7"/>
                                                </div>
                                                <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    {productionLabel}
                                                </span>
                                            </div>
                                            
                                           {/* DATE STRIP INSIDE THE BOX */}
                                            <span style={{ fontSize: '9px', color: '#475569', fontWeight: '700', background: '#F8FAFC', padding: '4px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <CalendarDays size={10} color="#64748B" /> 
                                                {activeDateStr.split(/\s+-\s+|\s+TO\s+|-TO-/).length === 2 ? (
                                                    <>
                                                        {activeDateStr.split(/\s+-\s+|\s+TO\s+|-TO-/)[0]}
                                                        <span style={{ fontSize: '9px', opacity: 0.35, margin: '0 5px', fontWeight: '500' }}>TO</span>
                                                        {activeDateStr.split(/\s+-\s+|\s+TO\s+|-TO-/)[1]}
                                                    </>
                                                ) : activeDateStr}
                                            </span>
                                        </div>
                                        
                                        {/* Production Value Section & Active Days Strip */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%', marginTop: '2px' }}>
                                           <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                                <span style={{ fontSize: '24px', fontWeight: '800', color: '#0F172A', lineHeight: '1' }}>
                                                    {currentTotal.toFixed(1)}
                                                </span>
                                                <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '700', letterSpacing: '0.5px', marginRight: '8px' }}>
                                                    METER
                                                </span>
                                                <span className="force-green" style={{ fontSize: '18px', fontWeight: '800', color: '#10B981', lineHeight: '1', marginLeft: '4px' }}>
                                                    ₹{loomTimeFilter === 'total' 
                                                        ? trueWorkerTotalEarnings.toFixed(0) 
                                                        : Number(currentTotal * safeParse(myLiveApiData.rate_per_meter || myLiveApiData.ratePerMeter || 1.1)).toFixed(0)
                                                    }
                                                </span>
                                            </div>
                                            
                                          {/* 🔥 NAYA: Sirf tabhi dikhega jab filter 'today' NAHI hoga */}
                                          {loomTimeFilter !== 'today' && (
                                              <span style={{ fontSize: '9px', color: '#059669', fontWeight: '700', background: '#E6FDF5', padding: '4px 10px', borderRadius: '8px', border: '1px solid #A7F3D0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                  <Activity size={10} color="#059669" /> {currentActiveDays} Active Days
                                              </span>
                                          )}
                                        </div>
                                        
                                    </div>
                                </div>

                               {/* 🌟 VIEW DETAILED REPORT BUTTON 🌟 */}
                                {loomTimeFilter !== 'today' && (
                                    <div 
                                        onClick={async (e) => {
                                            if (isReportLoading) return;
                                            
                                            let modalPeriod = loomTimeFilter;
                                            let detailedReportVoiceMsg = "आपकी पर्सनल डिटेल्ड रिपोर्ट डेटाबेस से लोड हो रही है, कृपया प्रतीक्षा करें।";
                                            
                                            const fetchWorkerSpecificData = async () => {
                                                setIsReportLoading(true);
                                                try {
                                                    const res = await fetch(`${API_BASE}/get_worker_stats.php?id=${user.id}&doj=${user.doj || ''}&t=${Date.now()}`);
                                                    const workerSpecificStats = await res.json();
                                                    
                                                    if(setStatsTab) setStatsTab(modalPeriod);
                                                    
                                                    setStatsModalData({ 
                                                        title: 'My Detailed Production', 
                                                        type: 'report', 
                                                        data: workerSpecificStats || { reports: { daily_wise: {}, month_wise: {}, year_wise: {} }, weekly: {} }, 
                                                        period: modalPeriod 
                                                    });
                                                } catch (error) {
                                                    console.error("Failed to fetch worker specific history", error);
                                                    alert("डेटा लोड करने में समस्या आई, कृपया इंटरनेट चेक करें।");
                                                } finally {
                                                    setIsReportLoading(false);
                                                }
                                            };

                                            if (handleVoiceClick) {
                                                handleVoiceClick(e, detailedReportVoiceMsg, fetchWorkerSpecificData, 'btn-detailed-report');
                                            } else {
                                                fetchWorkerSpecificData();
                                            }
                                        }} 
                                        style={{marginTop:'12px', paddingTop:'12px', borderTop:'1px solid #F1F5F9', cursor: isReportLoading ? 'wait' : 'pointer', display:'flex', justifyContent:'center'}}
                                    >
                                        <span style={{ 
                                            background: '#FFFFFF', 
                                            border: '1px solid #E2E8F0', 
                                            padding: '8px 16px', 
                                            borderRadius: '14px', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '6px', 
                                            fontSize: '10px', 
                                            color: isReportLoading ? '#94A3B8' : '#0F172A', 
                                            fontWeight: '800', 
                                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
                                            transition: 'all 0.3s'
                                        }}>
                                            {isReportLoading ? 'Loading Your Data...' : 'View My Detailed Report'} 
                                            <ChevronRight size={14} color={isReportLoading ? "#94A3B8" : "#2563EB"}/>
                                        </span>
                                    </div>
                                )}

                            </div>
                        );
                    })()}
                    
                </div>
            </div>

          {/* ===== 🌟 1000x ULTRA-PREMIUM SOLID LOOMS CARD (Platinum & Slate) 🌟 ===== */}
            {(() => {
                // 🌟 NAYA LOGIC: Assigned Looms aur Historical Looms ko combine karna 🌟
                const assignedIds = new Set(myLooms.map(l => String(l.loom_id || l.loom_no || l.id).replace(/[^0-9]/g, '')));
                const historicalIds = Object.keys(workerLoomStats).filter(id => workerLoomStats[id][loomTimeFilter] > 0);
                const combinedIds = Array.from(new Set([...assignedIds, ...historicalIds])).sort((a, b) => parseInt(a) - parseInt(b));
                
                const displayLooms = combinedIds.map(id => {
                    let found = myLooms.find(l => String(l.loom_id || l.loom_no || l.id).replace(/[^0-9]/g, '') === id);
                    if (found) return found;
                    // Agar purana loom hai to API ki active status lagane ke liye allLooms se uthao
                    found = allLooms.find(l => String(l.loom_id || l.loom_no || l.id).replace(/[^0-9]/g, '') === id);
                    return found || { loom_id: id };
                });

                return (
            <div className="animate-in" style={{ 
                marginTop: '24px', 
                animationDelay: '0.2s', 
                background: '#FFFFFF', 
                border: '1px solid #F1F5F9', 
                borderRadius: '24px', 
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.02)', 
                overflow: 'hidden' 
            }}>
                
                <div style={{ padding: '20px', background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{display:'flex', flexDirection: 'column'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            <div style={{ background: '#0F172A', padding: '6px', borderRadius: '10px' }}>
                                <Factory size={16} color="#FFFFFF"/>
                            </div>
                            <span style={{fontSize:'14px', fontWeight:'900', color:'#0F172A', letterSpacing: '-0.3px'}}>
                                {loomTimeFilter === 'today' ? 'My Assigned Looms' : 'My Production Looms'}
                            </span>
                        </div>
                        
                        <div style={{ marginTop: '4px', paddingLeft: '32px' }}>
                            <span style={{ fontSize: '9px', color: '#475569', fontWeight: '700', background: '#FFFFFF', padding: '4px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <CalendarDays size={10} color="#64748B" /> 
                                {activeDateStr.split(/\s+-\s+|\s+TO\s+|-TO-/).length === 2 ? (
                                    <>
                                        {activeDateStr.split(/\s+-\s+|\s+TO\s+|-TO-/)[0]}
                                        <span style={{ fontSize: '9px', opacity: 0.35, margin: '0 5px', fontWeight: '500' }}>TO</span>
                                        {activeDateStr.split(/\s+-\s+|\s+TO\s+|-TO-/)[1]}
                                    </>
                                ) : activeDateStr}
                            </span>
                        </div>
                    </div>
                    

                    <div style={{ background: '#FFFFFF', borderRadius: '14px', padding: '6px 12px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 10px rgba(16,185,129,0.6)' }} />
                        <span style={{ fontSize: '10px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase' }}>
                            <span style={{ color: '#0F172A', fontSize: '14px', fontWeight: '900' }}>{Number(displayLooms.filter(loom => isLoomActive(loom?.last_updated)).length)}</span> / {Number(displayLooms.length)} Active
                        </span>
                    </div>
                </div>

                <div style={{ padding: '20px', background: '#FFFFFF' }}>
                    {displayLooms.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                            <AlertCircle size={32} color="#94A3B8" />
                           <div style={{ color: '#64748B', fontWeight: '700', fontSize: '13px' }}>
                                No production looms found for {String(user?.name || 'Worker')}.<br/>Please contact owner.
                            </div>
                        </div>
                    ) : (
                       <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', width: '100%' }}>
                            {displayLooms.map((loom, idx) => {
                                if(!loom) return null;
                                const loomIdLabel = String(loom.loom_id || loom.loom_no || loom.id || (idx+1)).replace(/[^0-9]/g, '');
                                const isActive = isLoomActive(loom.last_updated);
                                
                              // 🌟 NAYA LOGIC: Check karein ki kya ye loom abhi current assigned list me hai
                              const isHistorical = !assignedIds.has(loomIdLabel);
                                
                              const workerShiftNameHindi = myLiveApiData.shift === 'Night' ? ' नाईट' : ' दिन';
                              const isNightWorker = myLiveApiData.shift === 'Night';

                              const workerShiftProd = workerLoomStats[loomIdLabel] ? Number(workerLoomStats[loomIdLabel][loomTimeFilter] || 0) : 0;
                              const trueDayProd = !isNightWorker ? workerShiftProd : 0;
                              const trueNightProd = isNightWorker ? workerShiftProd : 0;

                              let timeLabel = loomTimeFilter === 'today' ? ' आज का' : 
                                              loomTimeFilter === 'week' ? ' इस हफ्ते का' : 
                                              loomTimeFilter === 'month' ? ' इस महीने का' : ' टोटल';

                              let safeProd = Number(workerShiftProd).toFixed(1);
                              let voiceProd = safeProd.replace('.', ' point ');

                              let loomBoxVoiceMsg = ` लूम नंबर ${loomIdLabel} ${isActive ? ' अभी चालू है।' : ' अभी बंद है।'}, इस पर आपके ${workerShiftNameHindi} शिफ्ट का ${timeLabel} प्रोडक्शन ${voiceProd} मीटर ${isActive ? ' चल रहा है' : ' हुआ है'}।`;

                              return (
                                  <div key={idx} onClick={(e) => handleVoiceClick && handleVoiceClick(e, loomBoxVoiceMsg, null)} 
                                      style={{ 
                                          background: '#FFFFFF', borderRadius: '18px', padding: '14px 10px', 
                                          boxShadow: '0 4px 16px rgba(15, 23, 42, 0.03)', border: '1px solid #E2E8F0', 
                                          display: 'flex', flexDirection: 'column', minWidth: '0', cursor:'pointer',
                                          transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden',
                                          opacity: isHistorical ? 0.55 : 1 /* 🌟 FADE EFFECT YAHAN ADD HUA HAI */
                                      }}>                      
                                      
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', padding: '0 4px' }}>
                                          <h3 style={{ fontSize: '11px', fontWeight: '800', color: '#94A3B8', margin: 0, letterSpacing: '0.5px' }}>LOOM {String(loomIdLabel).padStart(2, '0')}</h3>
                                          {isActive ? (
                                              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 6px rgba(16, 185, 129, 0.8)' }}></span>
                                              </div>
                                          ) : (
                                              <span style={{ fontSize: '8px', fontWeight: '700', color: '#64748B', background: '#F1F5F9', padding: '2px 4px', borderRadius: '6px', letterSpacing: '0.5px' }}>OFF</span>
                                          )}
                                      </div>
                                      
                                      {(() => {
                                          const hasDayProd = trueDayProd > 0;
                                          const hasNightProd = trueNightProd > 0;

                                          return (
                                              <div style={{ display: 'flex', width: '100%', flex: 1, alignItems: 'flex-end' }}>
                                                  {hasDayProd && (
                                                      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 4px', borderRadius: '12px', transition: 'all 0.3s ease', 
                                                          background: !isNightWorker ? '#0F172A' : '#F8FAFC', 
                                                          border: !isNightWorker ? '1px solid #1E293B' : '1px solid #E2E8F0',
                                                          boxShadow: !isNightWorker ? '0 4px 12px rgba(15, 23, 42, 0.15)' : 'none'
                                                      }}>
                                                          <div style={{ fontSize: '8px', fontWeight: '700', marginBottom: '4px', letterSpacing:'1px', color: !isNightWorker ? '#94A3B8' : '#64748B' }}>DAY SHIFT</div>
                                                          <div style={{ fontSize: '15px', fontWeight: '800', color: !isNightWorker ? '#FFFFFF' : '#0F172A' }}>{trueDayProd.toFixed(1)}</div>
                                                      </div>
                                                  )}
                                                  
                                                  {hasNightProd && (
                                                      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 4px', borderRadius: '12px', transition: 'all 0.3s ease', 
                                                          background: isNightWorker ? '#0F172A' : '#F8FAFC', 
                                                          border: isNightWorker ? '1px solid #1E293B' : '1px solid #E2E8F0',
                                                          boxShadow: isNightWorker ? '0 4px 12px rgba(15, 23, 42, 0.15)' : 'none'
                                                      }}>
                                                          <div style={{ fontSize: '8px', fontWeight: '700', marginBottom: '4px', letterSpacing:'1px', color: isNightWorker ? '#94A3B8' : '#64748B' }}>NIGHT SHIFT</div>
                                                          <div style={{ fontSize: '15px', fontWeight: '800', color: isNightWorker ? '#FFFFFF' : '#0F172A' }}>{trueNightProd.toFixed(1)}</div>
                                                      </div>
                                                  )}

                                                  {!hasDayProd && !hasNightProd && (
                                                      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 4px', borderRadius: '12px', border: '1px dashed #CBD5E1', background: '#F8FAFC' }}>
                                                          <div style={{ fontSize: '9px', fontWeight: '800', color: '#94A3B8', opacity: 0.7, letterSpacing: '0.8px' }}>NO PRODUCTION</div>
                                                      </div>
                                                  )}
                                              </div>
                                          );
                                      })()}
                                 </div>
                              );
                            })}
                        </div>
                    )}
                </div>
            </div>
            );
            })()}
 
           <div style={{height: '60px'}}></div>
            
           {/* 🌟 RENDER STATS MODAL 🌟 */}
{statsModalData && (
    <StatsModal 
        title={statsModalData.title} 
        data={statsModalData.data} 
        period={statsModalData.period} 
        onClose={() => setStatsModalData(null)} 
        appBg={appBg} 
        handleVoiceClick={handleVoiceClick} 
        workerLooms={myLooms}           
        workerShift={myLiveApiData.shift} 
        myLiveApiData={myLiveApiData} 
    />
)}
        </div>       
    );
}

const styles = {
    content: { 
        padding: '24px 16px',
        maxWidth: '600px', 
        margin: '0 auto',
        minHeight: '100vh',
        position: 'relative',
        zIndex: 1
    }
};
